import React, { useRef, useMemo } from "react";
import DataTable, { TitleTmpl, reactFormatter } from "./DataTable";

interface Column {
  label: string;
  sortKey?: string;
  width?: string;
  render: (row: any, index: number) => React.ReactNode;
}

interface PerformerTableProps {
  columns: Column[];
  data: any[];
  rowKey?: (row: any, index: number) => string;
  count: number;
  page: number;
  rowsPerPage: number;
  activeSort: string;
  sortType: "asc" | "desc";
  onSort: (sortKey: string) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  emptyMessage?: string;
  hidePagination?: boolean;
  /** When provided, a native Tabulator rowSelection column is prepended and this callback fires on selection change */
  onSelectionChange?: (selectedRows: any[]) => void;
}

function PerformerCell({ cell, renderFn }: { cell?: any; renderFn: (row: any, index: number) => React.ReactNode }) {
  const row = cell ? cell.getData() : {};
  const index = cell ? (cell.getRow().getPosition(true) || 0) : 0;
  const result = renderFn(row, index);
  if (result === null || result === undefined || result === '') {
    return <>-</>;
  }
  return <>{result}</>;
}

function PerformerTable({
  columns,
  data,
  count,
  page,
  rowsPerPage,
  onSort,
  onPageChange,
  onRowsPerPageChange,
  hidePagination = false,
  onSelectionChange,
}: PerformerTableProps) {
  // Keep latest columns in a ref so cell renderers always call the freshest render function
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const onSortRef = useRef(onSort);
  onSortRef.current = onSort;

  // Only recreate Tabulator column definitions when the set of labels changes
  const columnLabels = columns.map((c) => c.label).join("|");

  const tabulatorColumns = useMemo(() => {
    const selectionCol = onSelectionChange
      ? [{
          formatter: "rowSelection",
          titleFormatter: "rowSelection",
          width: 40,
          headerSort: false,
          hozAlign: "center",
          headerHozAlign: "center",
        }]
      : [];

    const dataCols = columnsRef.current.map((col, colIndex) => {
      const colDef: any = {
        title: col.label,
        field: String(colIndex),
        headerSort: false,
        titleFormatter: reactFormatter(<TitleTmpl />),
        formatter: reactFormatter(null, (_cell: any) => {
          const currentCol = columnsRef.current[colIndex];
          if (!currentCol) return <span />;
          return <PerformerCell renderFn={currentCol.render} />;
        }),
      };
      if (col.width) colDef.width = parseInt(col.width);
      if (col.sortKey) {
        colDef.headerClick = (e: any) => {
          e.stopPropagation();
          onSortRef.current(col.sortKey!);
        };
      }
      return colDef;
    });

    return [...selectionCol, ...dataCols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnLabels, !!onSelectionChange]);

  const tabulatorData = useMemo(() => ({
    data: (data || []).map((row) => ({ ...row })),
    page: page + 1,
    last_page: Math.ceil(count / rowsPerPage) || 1,
    total: count,
  }), [data, count, page, rowsPerPage]);

  // Stable ajaxRequestFunc via ref — returns latest data on Tabulator init
  const tabulatorDataRef = useRef(tabulatorData);
  tabulatorDataRef.current = tabulatorData;
  const ajaxRequestFunc = useRef(() => Promise.resolve(tabulatorDataRef.current)).current;

  const tableOptions = useMemo(() => ({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns: tabulatorColumns,
    rowHeight: "auto",
    selectable: onSelectionChange ? true : false,
    paginate: !hidePagination,
    paginationSize: rowsPerPage,
    layout: "fitColumns",
  }), [tabulatorColumns, hidePagination, rowsPerPage, onSelectionChange]);

  function handlePageNav({ page: newPage, per }: { page: number; per: number }) {
    onPageChange(null, newPage - 1);
    if (per !== rowsPerPage) {
      onRowsPerPageChange({ target: { value: String(per) } } as React.ChangeEvent<HTMLInputElement>);
    }
  }

  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const handleSelectionChange = useRef((data: any[]) => {
    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current(data);
    }
  }).current;

  return (
    <div className="performer-table h-100">
      <DataTable
        data={tabulatorData}
        options={tableOptions}
        onPageNav={handlePageNav}
        onSelectionChange={onSelectionChange ? handleSelectionChange : undefined}
      />
    </div>
  );
}

export default PerformerTable;
